import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Building2, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STEPS = [
  'Overujem stredisko v TiDo…',
  'Sťahujem nastavenia strediska (otváracie hodiny, zmeny, pokrytie)…',
  'Importujem zamestnancov…',
  'Pripravujem plánovanie…',
];

export default function UvitaniePage() {
  const { user, setNeedsUvitanie } = useAuth();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return; // StrictMode-safe
    startedRef.current = true;
    let timer;
    const advance = () => {
      setStep((s) => (s < STEPS.length - 1 ? s + 1 : s));
      timer = setTimeout(advance, 1400);
    };
    timer = setTimeout(advance, 1400);
    (async () => {
      try {
        const now = new Date();
        const { data } = await api.post('/tido/uvitanie', {
          rok: now.getFullYear(), mesiac: now.getMonth() + 1,
        });
        clearTimeout(timer);
        setStep(STEPS.length - 1);
        setSummary(data);
        setDone(true);
      } catch (e) {
        clearTimeout(timer);
        const d = e?.response?.data?.detail;
        setError(typeof d === 'string' ? d : 'Import strediska zlyhal.');
      }
    })();
    return () => clearTimeout(timer);
  }, []);

  const goPlan = () => setNeedsUvitanie(false);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="uvitanie-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center">
            <Building2 className="w-6 h-6" />
          </div>
          <div>
            <div className="font-bold text-slate-900 text-lg leading-tight">Vitajte</div>
            <div className="text-sm text-slate-500" data-testid="uvitanie-center-name">{user?.username || 'Nové stredisko'}</div>
          </div>
        </div>

        {!error && (
          <div className="space-y-3 mb-6" data-testid="uvitanie-steps">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                {i < step || done ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                ) : i === step ? (
                  <Loader2 className="w-5 h-5 text-slate-400 animate-spin shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-slate-200 shrink-0" />
                )}
                <span className={i <= step || done ? 'text-slate-800' : 'text-slate-400'}>{s}</span>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div data-testid="uvitanie-error" className="flex items-start gap-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {done && summary && (
          <div data-testid="uvitanie-summary" className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mb-6">
            Stredisko <b>{summary.name}</b> je pripravené: {summary.employees_imported} zamestnancov, {summary.shifts} zmien.
          </div>
        )}

        <Button data-testid="uvitanie-continue" onClick={goPlan} disabled={!done && !error}
          className="w-full bg-slate-900 hover:bg-slate-800">
          {done ? 'Prejsť na plánovanie' : error ? 'Pokračovať aj tak' : 'Pripravujem…'}
        </Button>
      </div>
    </div>
  );
}
